import React, { useState, useEffect } from 'react';
import {
  Card,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  IconButton,
  Typography,
  Box,
  Alert,
  Tooltip
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Visibility as ShowIcon,
  VisibilityOff as HideIcon,
  SkipNext as JumpIcon,
  Stop as EndIcon,
  Message as MessageIcon
} from '@mui/icons-material';
import './LogicComponents.css';

const ActionBuilder = ({
  action,
  targetQuestions,
  currentQuestion,
  onChange,
  onDelete,
  errors,
  index
}) => {
  const [actionType, setActionType] = useState(action.type || 'show_question');
  const [targetId, setTargetId] = useState(action.targetId || '');
  const [value, setValue] = useState(action.value || '');

  // Update parent when local state changes
  useEffect(() => {
    onChange({
      ...action,
      type: actionType,
      targetId,
      value
    });
  }, [actionType, targetId, value]);

  const actionTypes = [
    { 
      value: 'show_question', 
      label: 'Show Question',
      icon: <ShowIcon fontSize="small" />,
      description: 'Make a question visible',
      requiresTarget: true,
      requiresValue: false
    },
    { 
      value: 'hide_question', 
      label: 'Hide Question',
      icon: <HideIcon fontSize="small" />,
      description: 'Hide a question from view',
      requiresTarget: true,
      requiresValue: false
    },
    { 
      value: 'jump_to', 
      label: 'Jump to Question',
      icon: <JumpIcon fontSize="small" />,
      description: 'Skip to a specific question',
      requiresTarget: true,
      requiresValue: false
    },
    { 
      value: 'end_survey', 
      label: 'End Survey',
      icon: <EndIcon fontSize="small" />,
      description: 'Complete the survey early',
      requiresTarget: false,
      requiresValue: false
    },
    { 
      value: 'show_message', 
      label: 'Show Message',
      icon: <MessageIcon fontSize="small" />,
      description: 'Display a custom message',
      requiresTarget: false,
      requiresValue: true
    }
  ];

  const selectedActionType = actionTypes.find(type => type.value === actionType);
  const selectedTargetQuestion = targetQuestions.find(q => q.id === targetId);

  const getErrorForField = (field) => {
    return errors[`action_${index}_${field}`];
  };

  const renderTargetSelection = () => {
    if (!selectedActionType?.requiresTarget) {
      return null;
    }

    return (
      <FormControl fullWidth size="small" error={!!getErrorForField('target')}>
        <InputLabel>Target Question</InputLabel>
        <Select
          value={targetId}
          onChange={(e) => setTargetId(e.target.value)}
          MenuProps={{
            PaperProps: {
              style: {
                maxHeight: 300,
              },
            },
          }}
        >
          {targetQuestions.map((q, index) => {
            const questionText = q.question_text || q.title || `Question ${index + 1}`;
            return (
              <MenuItem key={q.id || index} value={q.id || `temp-${index}`}>
                <Tooltip title={questionText} placement="top">
                  <Box sx={{ width: '100%', overflow: 'hidden' }}>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        fontWeight: 500,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        maxWidth: '250px'
                      }}
                    >
                      {questionText}
                    </Typography>
                    <Typography 
                      variant="caption" 
                      color="text.secondary"
                      sx={{ 
                        textTransform: 'capitalize',
                        fontSize: '0.75rem'
                      }}
                    >
                      {q.type?.replace('-', ' ')} • Position {index + 1}
                    </Typography>
                  </Box>
                </Tooltip>
              </MenuItem>
            );
          })}
        </Select>
        {getErrorForField('target') && (
          <Typography variant="caption" color="error">
            {getErrorForField('target')}
          </Typography>
        )}
      </FormControl>
    );
  };

  const renderValueInput = () => {
    if (!selectedActionType?.requiresValue) {
      return null;
    }

    switch (actionType) {
      case 'show_message':
        return (
          <TextField
            fullWidth
            size="small"
            label="Message"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            multiline
            rows={2}
            placeholder="Enter the message to display to respondents"
            error={!!getErrorForField('message')}
            helperText={getErrorForField('message')}
          />
        );
      
      default:
        return (
          <TextField
            fullWidth
            size="small"
            label="Value"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            error={!!getErrorForField('value')}
            helperText={getErrorForField('value')}
          />
        );
    }
  };

  const getActionPreview = () => {
    switch (actionType) {
      case 'show_question':
        return selectedTargetQuestion 
          ? `Show "${selectedTargetQuestion.question_text || selectedTargetQuestion.title}"`
          : 'Show a question';
      
      case 'hide_question':
        return selectedTargetQuestion 
          ? `Hide "${selectedTargetQuestion.question_text || selectedTargetQuestion.title}"`
          : 'Hide a question';
      
      case 'jump_to':
        return selectedTargetQuestion 
          ? `Jump to "${selectedTargetQuestion.question_text || selectedTargetQuestion.title}"`
          : 'Jump to a question';
      
      case 'end_survey':
        return 'End the survey and show completion page';
      
      case 'show_message':
        return value ? `Show message: "${value}"` : 'Show a custom message';
      
      default:
        return 'Unknown action';
    }
  };

  return (
    <Card sx={{ p: 2, mb: 2, bgcolor: 'success.50', border: '1px solid', borderColor: 'success.200' }}>
      <Grid container spacing={2} alignItems="flex-start">
        {/* Action Type Selection */}
        <Grid item xs={12} sm={4}>
          <FormControl fullWidth size="small" error={!!getErrorForField('type')}>
            <InputLabel>Action Type</InputLabel>
            <Select
              value={actionType}
              onChange={(e) => {
                setActionType(e.target.value);
                // Reset target and value when action type changes
                setTargetId('');
                setValue('');
              }}
            >
              {actionTypes.map(type => (
                <MenuItem key={type.value} value={type.value}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                    {type.icon}
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {type.label}
                      </Typography>
                      <Typography 
                        variant="caption" 
                        color="text.secondary"
                        sx={{ 
                          display: 'block',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        {type.description}
                      </Typography>
                    </Box>
                  </Box>
                </MenuItem>
              ))}
            </Select>
            {getErrorForField('type') && (
              <Typography variant="caption" color="error">
                {getErrorForField('type')}
              </Typography>
            )}
          </FormControl>
        </Grid>

        {/* Target Selection */}
        {selectedActionType?.requiresTarget && (
          <Grid item xs={12} sm={4}>
            {renderTargetSelection()}
          </Grid>
        )}

        {/* Value Input */}
        {selectedActionType?.requiresValue && (
          <Grid item xs={12} sm={3}>
            {renderValueInput()}
          </Grid>
        )}

        {/* Delete Button */}
        <Grid item xs={12} sm={1}>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', height: '100%', alignItems: 'flex-start', pt: 1 }}>
            <IconButton 
              onClick={onDelete} 
              size="small" 
              color="error"
            >
              <DeleteIcon />
            </IconButton>
          </Box>
        </Grid>
      </Grid>

      {/* Action Preview */}
      <Box sx={{ mt: 2, p: 1, bgcolor: 'success.100', borderRadius: 1 }}>
        <Typography variant="caption" color="success.dark">
          Preview: {getActionPreview()}
        </Typography>
      </Box>

      {/* Warnings and Tips */}
      {actionType === 'end_survey' && (
        <Alert severity="warning" sx={{ mt: 2 }}>
          This will end the survey immediately. Respondents will not see any subsequent questions.
        </Alert>
      )}

      {actionType === 'jump_to' && (
        <Alert severity="info" sx={{ mt: 2 }}>
          This will skip all questions between the current question and the target question.
        </Alert>
      )}

      {selectedActionType?.requiresTarget && targetQuestions.length === 0 && (
        <Alert severity="warning" sx={{ mt: 2 }}>
          No target questions available. Add more questions after this one to use this action.
        </Alert>
      )}
    </Card>
  );
};

export default ActionBuilder;